import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import {
  AlertCircle,
  CheckCircle,
  Loader2,
  RefreshCw,
  Search,
  ShieldCheck,
  UserCog,
} from 'lucide-react';
import { functions } from '../../firebase';

const listRoleUsers = httpsCallable(functions, 'listRoleUsers');
const setUserRoles = httpsCallable(functions, 'setUserRoles');

const AdminPage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [savingEmail, setSavingEmail] = useState('');
  const [search, setSearch] = useState('');
  const [email, setEmail] = useState('');
  const [newRoles, setNewRoles] = useState({ admin: false, hr: true });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const result = await listRoleUsers();
      setUsers(result.data.users);
    } catch (loadError) {
      console.error('Error loading role users:', loadError);
      setError('No se pudieron cargar los usuarios y sus roles.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  const filteredUsers = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return users;
    return users.filter((user) =>
      `${user.displayName} ${user.email}`.toLowerCase().includes(term)
    );
  }, [search, users]);

  const updateRoles = async (targetEmail, roles) => {
    setSavingEmail(targetEmail);
    setError('');
    setSuccess('');
    try {
      await setUserRoles({ email: targetEmail, roles });
      setSuccess(
        `Roles actualizados para ${targetEmail}. La persona debe cerrar sesión y volver a entrar para aplicar el cambio.`
      );
      await loadUsers();
    } catch (updateError) {
      console.error('Error updating roles:', updateError);
      setError(
        updateError.message ||
        'No se pudieron actualizar los roles. El usuario debe haber iniciado sesión al menos una vez.'
      );
    } finally {
      setSavingEmail('');
    }
  };

  const handleGrantByEmail = async (event) => {
    event.preventDefault();
    const normalizedEmail = email.trim().toLowerCase();
    if (!/^[^@\s]+@sonneil\.com$/.test(normalizedEmail)) {
      setError('Introduce un email corporativo @sonneil.com válido.');
      return;
    }
    await updateRoles(normalizedEmail, newRoles);
    setEmail('');
  };

  const handleExistingRoleChange = (targetUser, role, enabled) => {
    if (!enabled) {
      const roleName = role === 'admin' ? 'administrador' : 'RRHH';
      if (!window.confirm(
        `¿Retirar el rol de ${roleName} a ${targetUser.email}?`
      )) {
        return;
      }
    }

    updateRoles(targetUser.email, {
      admin: role === 'admin' ? enabled : targetUser.admin,
      hr: role === 'hr' ? enabled : targetUser.hr,
    });
  };

  return (
    <div className="animate-stagger">
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <ShieldCheck size={28} className="text-primary" />
          <h1 className="text-2xl font-bold text-text-primary">Administración de roles</h1>
        </div>
        <p className="text-text-secondary">
          Los administradores pueden asignar acceso de administración o RRHH.
        </p>
      </div>

      {error && (
        <div className="mb-6 p-4 bg-error/10 text-error rounded-xl flex items-start gap-2" role="alert">
          <AlertCircle size={19} className="flex-shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="mb-6 p-4 bg-success/10 text-success rounded-xl flex items-center gap-2" role="status">
          <CheckCircle size={19} />
          <span>{success}</span>
        </div>
      )}

      <section id="asignar-roles" className="card p-6 border border-border mb-8 scroll-mt-24">
        <div className="flex items-start gap-3 mb-5">
          <UserCog size={22} className="text-primary mt-0.5" />
          <div>
            <h2 className="text-lg font-semibold text-text-primary">Asignar por email</h2>
            <p className="text-sm text-text-secondary mt-1">
              El usuario debe haber accedido a la intranet al menos una vez para existir en Firebase Authentication.
            </p>
          </div>
        </div>
        <form onSubmit={handleGrantByEmail} className="grid grid-cols-1 lg:grid-cols-[1fr_auto_auto_auto] gap-4 items-end">
          <label>
            <span className="block text-sm font-medium text-text-primary mb-2">Email corporativo</span>
            <input
              type="email"
              className="input"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="persona@sonneil.com"
              required
            />
          </label>
          <label className="flex items-center gap-2 h-12 px-3">
            <input
              type="checkbox"
              checked={newRoles.hr}
              onChange={(event) => setNewRoles({ ...newRoles, hr: event.target.checked })}
              className="rounded border-border text-primary focus:ring-primary"
            />
            <span className="text-sm font-medium text-text-primary">RRHH</span>
          </label>
          <label className="flex items-center gap-2 h-12 px-3">
            <input
              type="checkbox"
              checked={newRoles.admin}
              onChange={(event) => setNewRoles({ ...newRoles, admin: event.target.checked })}
              className="rounded border-border text-primary focus:ring-primary"
            />
            <span className="text-sm font-medium text-text-primary">Admin</span>
          </label>
          <button
            type="submit"
            disabled={savingEmail === email.trim().toLowerCase()}
            className="btn-primary h-12 disabled:opacity-50"
          >
            {savingEmail === email.trim().toLowerCase() && <Loader2 size={17} className="animate-spin" />}
            Aplicar roles
          </button>
        </form>
      </section>

      <section>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-5">
          <div>
            <h2 id="usuarios-registrados" className="text-xl font-semibold text-text-primary scroll-mt-24">Usuarios registrados</h2>
            <p className="text-sm text-text-secondary mt-1">{users.length} cuentas corporativas</p>
          </div>
          <button type="button" onClick={loadUsers} className="btn-secondary btn-sm" disabled={loading}>
            <RefreshCw size={17} className={loading ? 'animate-spin' : ''} />
            Actualizar
          </button>
        </div>

        <div className="relative max-w-lg mb-5">
          <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-tertiary" />
          <input
            className="input pl-11"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar usuarios"
            aria-label="Buscar usuarios"
          />
        </div>

        {loading ? (
          <div className="py-16 flex justify-center text-primary">
            <Loader2 size={32} className="animate-spin" />
          </div>
        ) : (
          <div className="card border border-border overflow-x-auto">
            <table className="w-full min-w-[680px]">
              <thead className="bg-background border-b border-border">
                <tr>
                  <th className="text-left px-5 py-3 text-sm font-semibold text-text-secondary">Usuario</th>
                  <th className="text-center px-5 py-3 text-sm font-semibold text-text-secondary">RRHH</th>
                  <th className="text-center px-5 py-3 text-sm font-semibold text-text-secondary">Admin</th>
                  <th className="text-right px-5 py-3 text-sm font-semibold text-text-secondary">Estado</th>
                </tr>
              </thead>
              <tbody>
                {filteredUsers.map((user) => {
                  const isSaving = savingEmail === user.email;
                  return (
                    <tr key={user.uid} className="border-b last:border-b-0 border-border-light">
                      <td className="px-5 py-4">
                        <p className="font-medium text-text-primary">{user.displayName || 'Sin nombre'}</p>
                        <p className="text-sm text-text-tertiary">{user.email}</p>
                      </td>
                      <td className="px-5 py-4 text-center">
                        <input
                          type="checkbox"
                          checked={user.hr}
                          disabled={isSaving || user.disabled}
                          onChange={(event) =>
                            handleExistingRoleChange(user, 'hr', event.target.checked)
                          }
                          className="rounded border-border text-primary focus:ring-primary"
                          aria-label={`Rol de RRHH para ${user.email}`}
                        />
                      </td>
                      <td className="px-5 py-4 text-center">
                        <input
                          type="checkbox"
                          checked={user.admin}
                          disabled={isSaving || user.disabled}
                          onChange={(event) =>
                            handleExistingRoleChange(user, 'admin', event.target.checked)
                          }
                          className="rounded border-border text-primary focus:ring-primary"
                          aria-label={`Rol de administrador para ${user.email}`}
                        />
                      </td>
                      <td className="px-5 py-4 text-right">
                        {isSaving ? (
                          <Loader2 size={18} className="animate-spin text-primary ml-auto" />
                        ) : user.disabled ? (
                          <span className="badge-error">Deshabilitado</span>
                        ) : user.admin ? (
                          <span className="badge-info">Administrador</span>
                        ) : user.hr ? (
                          <span className="badge-success">RRHH</span>
                        ) : (
                          <span className="badge">Empleado</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {filteredUsers.length === 0 && (
              <p className="p-8 text-center text-text-secondary">No hay usuarios que coincidan.</p>
            )}
          </div>
        )}
      </section>
    </div>
  );
};

export default AdminPage;
